import { Box, Typography, Button } from '@mui/material'
import { Lock } from '@mui/icons-material'
import { COLORS, TIER_COLORS } from '../../constants/colors'
import { TIERS, TIER_CONFIG } from '../../constants/tiers'
import { useTier } from '../../hooks/useTier'
import { TEXT } from '../../constants/text'

/**
 * Wraps content that requires a minimum tier.
 * If user's tier is lower, shows a blurred overlay with upgrade CTA.
 *
 * @param {string} requiredTier  TIERS.PRO | TIERS.ELITE
 * @param {React.ReactNode} children
 * @param {function} onUpgrade  callback when user clicks Upgrade
 */
export default function TierGate({ requiredTier, children, onUpgrade }) {
  const { isAtLeast } = useTier()

  if (isAtLeast(requiredTier)) {
    return children
  }

  const requiredConfig = TIER_CONFIG[requiredTier]
  const tierColor = TIER_COLORS[requiredTier]

  return (
    <Box sx={{ position: 'relative' }}>
      {/* Blurred child content as teaser */}
      <Box
        sx={{
          filter: 'blur(8px)',
          pointerEvents: 'none',
          userSelect: 'none',
          opacity: 0.5,
        }}
      >
        {children}
      </Box>

      {/* Overlay */}
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 1.5,
          background: 'rgba(0,0,0,0.6)',
          backdropFilter: 'blur(2px)',
          borderRadius: 2,
        }}
      >
        <Lock sx={{ fontSize: 32, color: tierColor.primary, opacity: 0.8 }} />

        <Typography
          sx={{
            fontSize: '0.85rem',
            color: COLORS.text.secondary,
            textAlign: 'center',
            px: 2,
          }}
        >
          {TEXT.TIERS.LOCKED}
        </Typography>

        <Button
          variant="outlined"
          size="small"
          onClick={onUpgrade}
          sx={{
            color: tierColor.primary,
            borderColor: tierColor.border,
            textTransform: 'none',
            fontWeight: 600,
            '&:hover': {
              borderColor: tierColor.primary,
              background: tierColor.bg,
            },
          }}
        >
          {TEXT.TIERS.UPGRADE} to {requiredConfig.name} — ${requiredConfig.price}{TEXT.TIERS.PER_MONTH}
        </Button>
      </Box>
    </Box>
  )
}
